/**
 * strings.js — plain landing / guide strings ({ ar, en }), pure data.
 * ───────────────────────────────────────────────────────────────────
 * Arabic is copied verbatim from the approved mockup (v2/landing/index.html).
 * English reuses the existing EN copy where the meaning did not change.
 * Strings that need inline markup (LTR-isolated numbers, nowrap runs) live
 * as JSX in the section components; this file stays importable from tests.
 */

export const STR = {
  /* header sheet links (mockup .msheet) */
  menuHow: { ar: 'كيف تشتغل', en: 'How it works' },
  menuDemo: { ar: 'جرّب التصميم', en: 'Try the demo' },
  menuScan: { ar: 'مسح البطاقات', en: 'Card scanning' },
  menuBiz: { ar: 'للشركات', en: 'For business' },
  menuPricing: { ar: 'الأسعار', en: 'Pricing' },
  menuFaq: { ar: 'الأسئلة الشائعة', en: 'FAQ' },

  /* hero */
  announceAria: { ar: 'حمّل تطبيق CardPass من App Store', en: 'Get the CardPass app on the App Store' },
  heroTitle: { ar: 'بطاقتك تعيش في محفظتك.', en: 'Your business card lives in your wallet.' },
  heroCta: { ar: 'أنشئ بطاقتك مجانًا', en: 'Create your free card' },
  heroBiz: { ar: 'CardPass للشركات', en: 'CardPass for Business' },
  tick1: { ar: 'الخطة الأساسية مجانية', en: 'Base plan is free' },
  tick2: { ar: 'بدون بطاقة ائتمان', en: 'No credit card' },
  tick3: { ar: 'آيفون وأندرويد', en: 'iPhone and Android' },
  tick4: { ar: 'متوفر في السعودية', en: 'Available in Saudi Arabia' },

  /* hero stage */
  edTitle: { ar: 'صمّم بطاقتك', en: 'Design your card' },
  live: { ar: 'معاينة مباشرة', en: 'Live preview' },
  fName: { ar: 'الاسم', en: 'Name' },
  fTitle: { ar: 'المسمى الوظيفي', en: 'Job title' },
  fColor: { ar: 'لون البطاقة', en: 'Card color' },
  saved: { ar: 'انحفظت جهة الاتصال', en: 'Contact saved' },
  wallet: { ar: 'Wallet', en: 'Wallet' },

  /* how it works */
  howEb: { ar: 'كيف تشتغل', en: 'How it works' },
  howTitle: { ar: 'ثلاث خطوات. بدون ورق.', en: 'Three steps. No paper.' },
  suTitle: { ar: 'أنشئ حسابك', en: 'Create your account' },
  suNext: { ar: 'متابعة', en: 'Continue' },
  suOr: { ar: 'أو', en: 'or' },
  edColor: { ar: 'اللون', en: 'Color' },
  edLogo: { ar: 'شعارك', en: 'Logo' },
  edPhoto: { ar: 'صورتك', en: 'Photo' },
  step2T: { ar: 'صمّم بطاقتك مباشرة', en: 'Design your card live' },
  step2D: {
    ar: 'ارفع شعارك وصورتك، وعبّي بياناتك. المعاينة تتحدّث لحظيًا، وتطلع مثل بطاقة Wallet حقيقية لأنها فعلًا كذا.',
    en: 'Drop your logo and photo, and fill in your details. The preview updates in real time. It looks like a real Wallet pass because it is one.'
  },
  step3T: { ar: 'شاركها بنقرة', en: 'Share with one tap' },

  /* live demo */
  demoEb: { ar: 'تجربة حية', en: 'Live demo' },
  demoTitle: { ar: 'بطاقتك تتكوّن أمامك.', en: 'Watch your card come alive.' },
  demoSub: {
    ar: 'اكتب بياناتك، اختر لونك، وأضف شعارك وصورتك. كل خطوة تشوفها على البطاقة لحظتها.',
    en: 'Type your details, pick a color, add your logo and photo. You see every step on the card instantly.'
  },
  demoSteps: { ar: 'خطوات التصميم', en: 'Design steps' },
  d0T: { ar: 'تعديل فوري', en: 'Live editing' },
  d0D: { ar: 'اكتب بياناتك، تنعكس على البطاقة لحظيًا.', en: 'Type your details and they show on the card instantly.' },
  d1T: { ar: 'اختيار الألوان', en: 'Pick your color' },
  d1D: { ar: 'اختر لونًا، ويتغيّر شكل البطاقة فورًا.', en: 'Choose a color and the card restyles right away.' },
  d2T: { ar: 'شعارك وصورتك', en: 'Logo & photo' },
  d2D: { ar: 'أضف شعارك أو شعار شركتك، وأرفق صورتك.', en: 'Add your logo or your company’s logo, and attach your photo.' },
  seg0: { ar: 'تعديل', en: 'Edit' },
  seg1: { ar: 'اللون', en: 'Color' },
  seg2: { ar: 'شعارك وصورتك', en: 'Logo & photo' },
  cWhite: { ar: 'أبيض', en: 'White' },
  cDark: { ar: 'أسود', en: 'Black' },
  cBlue: { ar: 'أزرق', en: 'Blue' },
  cGreen: { ar: 'أخضر', en: 'Green' },
  cWine: { ar: 'عنابي', en: 'Burgundy' },
  upLogo: { ar: 'الشعار', en: 'Logo' },
  upPhoto: { ar: 'الصورة', en: 'Photo' },
  ready: { ar: 'جاهزة', en: 'Ready' },
  scanSave: { ar: 'امسح لحفظ جهة الاتصال', en: 'Scan to save contact' },
  worksWith: { ar: 'تشتغل مع', en: 'Works with' },

  /* scan */
  scanEb: { ar: 'ميزة مسح البطاقات', en: 'Card scanning feature' },
  scanP: {
    ar: 'بدل ما تشيل عشرات البطاقات وتدخّل بياناتها يدويًا، صوّر البطاقة مرة وحدة وخلّ CardPass يقرأ المعلومات ويرتّبها كجهة اتصال جاهزة للحفظ.',
    en: 'Instead of carrying piles of cards and typing every detail by hand, capture the card once and let CardPass extract the details into a contact ready to save.'
  },
  sf1: { ar: 'افتح الكاميرا', en: 'Open camera' },
  sf2: { ar: 'امسح البطاقة', en: 'Scan the card' },
  sf3: { ar: 'احفظها', en: 'Save' },
  sf3x: { ar: ' في جوالك', en: ' to phone' },
  ben1: { ar: 'بدون كتابة الأسماء والأرقام يدويًا', en: 'No manual typing for names or numbers' },
  ben2: { ar: 'تقرأ الإيميل، الجوال، الشركة والمسمى', en: 'Reads email, phone, company, and title' },
  ben3: { ar: 'تتحوّل لجهة اتصال جاهزة بدل كومة بطاقات', en: 'Turns paper stacks into ready contacts' },
  scanTry: { ar: 'جرّب مسح بطاقة', en: 'Try scanning a card' },
  camera: { ar: 'كاميرا', en: 'Camera' },
  paperName: { ar: 'محمد الزهراني', en: 'Mohammed Al-Zahrani' },
  paperRole: { ar: 'مؤسس CardPass', en: 'Founder, CardPass' },
  extTitle: { ar: 'تم استخراج البيانات', en: 'Details extracted' },
  extSub: { ar: 'جاهزة للحفظ كجهة اتصال', en: 'Ready to save as a contact' },
  exName: { ar: 'الاسم', en: 'Name' },
  exCompany: { ar: 'الشركة', en: 'Company' },
  exPhone: { ar: 'الجوال', en: 'Phone' },
  exEmail: { ar: 'الإيميل', en: 'Email' },
  exSave: { ar: 'احفظها في جهات الاتصال', en: 'Save to contacts' },

  /* meetings & booking */
  menuMeet: { ar: 'المواعيد', en: 'Meetings' },
  meetEb: { ar: 'الاجتماعات والحجز', en: 'Meetings and booking' },
  meetTitle: { ar: 'خلّ عملاءك يحجزون اجتماعهم معك من بطاقتك.', en: 'Let clients book a meeting with you right from your card.' },
  mf1: { ar: 'اربط تقويمك', en: 'Connect' },
  mf2: { ar: 'الزائر يختار وقت', en: 'Pick a time' },
  mf3: { ar: 'ينزل في تقويمك', en: 'Booked' },
  mben2: { ar: 'إيميل تأكيد لك وللزائر، ويقدر يغيّر الموعد أو يلغيه بنفسه', en: 'A confirmation email for you and the visitor, who can reschedule or cancel on their own' },
  mben3: { ar: 'تحدد أيامك وساعاتك ومدة كل اجتماع، ومواعيد تقويمك ما تتعارض مع الحجز', en: 'Set your days, hours and meeting lengths; nothing clashes with your calendar' },
  meetCta: { ar: 'فعّل الحجز في بطاقتك', en: 'Turn on booking for your card' },
  mBookWith: { ar: 'احجز اجتماع مع سارة', en: 'Book a meeting with Sara' },
  mMin: { ar: 'دقيقة', en: 'min' },
  am: { ar: 'ص', en: 'AM' },
  pm: { ar: 'م', en: 'PM' },
  mConfirm: { ar: 'أكّد الحجز', en: 'Confirm booking' },
  mBooked: { ar: 'تم الحجز', en: 'Booked' },
  mBookedSub: { ar: 'نزل في تقويم سارة ووصل الإيميل للطرفين', en: "In Sara's calendar, email sent to both" },
  mEvt: { ar: 'اجتماع تعريفي مع خالد', en: 'Intro meeting with Khalid' },
  mEvtWhen: { ar: 'الثلاثاء 27 · 10:30 ص', en: 'Tue 27 · 10:30 AM' },

  /* why wallet */
  whyEb: { ar: 'ليش Wallet', en: 'Why Wallet' },
  whyT1: { ar: 'الكروت الورقية تموت في الجيوب.', en: 'Paper cards die in pockets.' },
  whyT2: { ar: 'بطاقتك تعيش للأبد.', en: 'Yours lives forever.' },
  f1L: { ar: 'دايمًا معك', en: 'Always with you' },
  f1T: { ar: 'في محفظة جوالك', en: 'In your phone’s wallet' },
  f2L: { ar: 'تحديث تلقائي', en: 'Auto-sync' },
  f2T: { ar: 'غيّر مرة، يتحدّث في كل مكان', en: 'Change once, update everywhere' },
  f2D: {
    ar: 'عدّل مسماك أو رقمك من اللوحة، وكل بطاقة شاركتها تتحدّث خلال ثواني.',
    en: 'Edit your title or phone on the dashboard, and every card you shared updates within seconds.'
  },
  syn1: { ar: 'محفظتك', en: 'Your wallet' },
  syn2: { ar: 'صفحتك', en: 'Your page' },
  syn3: { ar: 'عند اللي حفظوها', en: 'Contacts' },
  f3L: { ar: 'مسح QR', en: 'QR scan' },
  f3T: { ar: 'نقرة تحفظ جهة اتصالك', en: 'One tap saves your contact' },
  f4L: { ar: 'ثنائي اللغة', en: 'Bilingual' },
  f4T: { ar: 'عربي وإنجليزي جاهز', en: 'Arabic and English ready' },
  f4D: {
    ar: 'اعرض اسمك ومسماك باللغتين. مصمّم للسوق السعودي والخليجي من اليوم الأول.',
    en: 'Display your name and title in both languages. Built for the Saudi and Gulf market from day one.'
  },
  f5L: { ar: 'تحليلات', en: 'Analytics' },
  f5T: { ar: 'شوف مين يطّلع', en: 'See who’s looking' },

  /* business */
  bizEb: { ar: 'للشركات', en: 'For Business' },
  bizTitle: { ar: 'بطاقات فريقك، أصول رقمية تشتغل لك.', en: 'Your team’s cards, digital assets that work for you.' },
  bizCta: { ar: 'اكتشف CardPass للشركات', en: 'Explore CardPass for Business' },
  pt1T: { ar: 'بطاقات للفريق كامل', en: 'Cards for your whole team' },
  pt1D: { ar: 'وحّد هوية شركتك على كل بطاقة موظف.', en: 'Unify your brand across every employee card.' },
  pt2T: { ar: 'لوحة تحكم للإدارة', en: 'Admin dashboard' },
  pt2D: { ar: 'أنشئ، عدّل، واسحب البطاقات من مكان واحد.', en: 'Create, edit, and revoke cards from one place.' },
  pt3T: { ar: 'توفير وتحليلات', en: 'Savings & analytics' },
  pt3D: { ar: 'وفّر تكاليف الطباعة وتابع التفاعل.', en: 'Cut print costs and track engagement.' },
  bizImg: { ar: 'لوحة تحكم CardPass للشركات', en: 'CardPass for Business dashboard' },
  bizImgM: { ar: 'لوحة تحكم CardPass للشركات على الجوال', en: 'CardPass for Business dashboard on a phone' },

  /* reviews */
  revEb: { ar: 'آراء العملاء', en: 'Testimonials' },
  revTitle: { ar: 'وش قالوا عنّا.', en: 'What our clients say.' },

  /* final CTA */
  ctaT1: { ar: 'بطّل تطبع.', en: 'Stop printing.' },
  ctaT2: { ar: 'ابدأ شارك.', en: 'Start sharing.' },
  ctaBtn: { ar: 'احصل على بطاقتك الآن', en: 'Get your card now' },
  ctaNote: { ar: 'الخطة الأساسية مجانية · بدون بطاقة ائتمان', en: 'Base plan is free · No credit card' },
  appStore: { ar: 'حمّل CardPass من App Store', en: 'Download CardPass on the App Store' },

  /* guides (SeoLanding) */
  toc: { ar: 'في هذا الدليل', en: 'In this guide' },
  related: { ar: 'أدلة ذات صلة', en: 'Related guides' },
  guide: { ar: 'دليل', en: 'Guide' },
  forBiz: { ar: 'CardPass للشركات', en: 'CardPass for Business' },
  faqToc: { ar: 'الأسئلة الشائعة', en: 'FAQ' }
};

/** Demo step order (desktop tablist + phone segmented control). */
export const DEMO_STEP_KEYS = [
  { t: 'd0T', d: 'd0D', seg: 'seg0' },
  { t: 'd1T', d: 'd1D', seg: 'seg1' },
  { t: 'd2T', d: 'd2D', seg: 'seg2' }
];

/** Pick a { ar, en } value for a locale (Arabic fallback). */
export const pick = (v, locale) => (v ? v[locale] ?? v.ar : '');
